import type { FacilityHierarchyInput } from "@utils/facilityUtils";
import { getFacilityHierarchyId } from "@utils/facilityUtils";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import type {
  NetworkDashboardMeta,
  SnfDashboardData,
  SnfDashboardFacility,
  SnfDashboardSummary,
} from "@services/networkDashboard";
import {
  abbreviateFacilityType,
  facilityLocation,
  formatCcnLabel,
  networkRegistryDisplayIds,
} from "./facilityNetworkChildrenUtils";

export const SNF_TYPE_CODE = "SNF";

export const SNF_DASHBOARD_SUBTITLE =
  "CMS 5-star rating · PBJ staffing · F-tag citation history · SNF VBP";

export const SNF_ROSTER_SUBTITLE =
  "SNF-specific parameters · click a facility to view its dashboard";

export type SnfTone = "error" | "warn" | "muted" | "neutral";

export type SnfKpiTile = {
  id: string;
  label: string;
  value: string;
  sublabel: string;
  valueTone?: SnfTone;
};

export type SnfRosterRow = {
  key: string;
  facilityId: number | null;
  name: string;
  metaLine: string;
  stars: number | null;
  hprdLabel: string;
  hprdTone: SnfTone;
  fTagsActive: number | null;
  ijCount: number | null;
  nextSurveyLabel: string;
  nextSurveyTone: SnfTone;
  estimatedAdjustmentLabel: string;
  missingSourcesLabel: string | null;
  dashboardFacility: MyFacilityListRow | null;
};

export type SnfDashboardModel = {
  facilityCount: number;
  kpis: SnfKpiTile[];
  roster: SnfRosterRow[];
};

function hprdTone(hprd: number | null): SnfTone {
  if (hprd == null) return "muted";
  return hprd < 3.0 ? "warn" : "muted";
}

function surveyTone(days: number | null): SnfTone {
  if (days == null) return "muted";
  if (days <= 30) return "error";
  if (days <= 90) return "warn";
  return "muted";
}

function formatCurrency(value: number | null): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatStars(value: number | null): string {
  return value == null ? "—" : `${value.toFixed(1)} / 5`;
}

function formatHprd(value: number | null): string {
  return value == null ? "—" : value.toFixed(2);
}

function formatSurveyDays(value: number | null): string {
  if (value == null) return "—";
  if (value < 0) return `${Math.abs(value)} days overdue`;
  return `${value} days`;
}

function toDashboardFacility(
  facility: FacilityHierarchyInput,
  facilityId: number,
): MyFacilityListRow {
  return {
    facility_id: facilityId,
    id: facilityId,
    facility_name: facility.facility_name || `Facility ${facilityId}`,
    city: typeof facility.city === "string" ? facility.city : "",
    state: typeof facility.state === "string" ? facility.state : "",
    full_address: facilityLocation(facility),
    facility_type:
      (typeof facility.facility_type === "string" && facility.facility_type) || "SNF",
    facility_type_slug:
      (typeof facility.facility_type_slug === "string" && facility.facility_type_slug) ||
      "skilled_nursing_facility",
    org_id: 0,
    org_name: "",
  };
}

export function collectLinkedSnfFacilities(
  members: FacilityHierarchyInput[],
  selectedId: number | null,
): FacilityHierarchyInput[] {
  return members.filter((member) => {
    const id = getFacilityHierarchyId(member);
    if (selectedId != null && id === selectedId) return false;
    return abbreviateFacilityType(member) === SNF_TYPE_CODE;
  });
}

function buildMetaLine(facility: FacilityHierarchyInput): string {
  const ids = networkRegistryDisplayIds(facility);
  const ccn = ids.ccn ? formatCcnLabel(ids.ccn) : "";
  const location = facilityLocation(facility);
  return [ccn, location !== "—" ? location : ""].filter(Boolean).join(" · ");
}

function normalizeCcn(value: string | null | undefined): string {
  return String(value || "").replace(/\D/g, "").padStart(6, "0");
}

function linkedFacilityByCcn(
  linkedSnfs: FacilityHierarchyInput[],
): Map<string, FacilityHierarchyInput> {
  const result = new Map<string, FacilityHierarchyInput>();
  linkedSnfs.forEach((facility) => {
    const ccn = networkRegistryDisplayIds(facility).ccn;
    if (ccn) result.set(normalizeCcn(ccn), facility);
  });
  return result;
}

function snfSummary(summary: Partial<SnfDashboardSummary> | null | undefined): SnfDashboardSummary {
  return {
    facility_count: summary?.facility_count ?? null,
    average_star_rating: summary?.average_star_rating ?? null,
    rated_facility_count: summary?.rated_facility_count ?? null,
    lowest_star_rating: summary?.lowest_star_rating ?? null,
    below_3_hprd_count: summary?.below_3_hprd_count ?? null,
    hprd_facility_count: summary?.hprd_facility_count ?? null,
    active_f_tag_count: summary?.active_f_tag_count ?? null,
    ij_count: summary?.ij_count ?? null,
    severity_d_plus_count: summary?.severity_d_plus_count ?? null,
    severity_a_c_count: summary?.severity_a_c_count ?? null,
    estimated_vbp_adjustment_ytd_usd: summary?.estimated_vbp_adjustment_ytd_usd ?? null,
    vbp_contributing_facility_count: summary?.vbp_contributing_facility_count ?? null,
    federal_fiscal_year: summary?.federal_fiscal_year ?? null,
  };
}

function rowName(row: SnfDashboardFacility, linked: FacilityHierarchyInput | undefined, index: number): string {
  return row.name || linked?.facility_name || `SNF ${index + 1}`;
}

function missingSourcesLabel(
  sources: string[] | undefined,
  ccn: string | null | undefined,
): string | null {
  const list = sources?.length ? sources : ccn ? [] : ["ccn"];
  if (!list.length) return null;
  const pretty = list.map((source) =>
    source.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()),
  );
  return `Missing: ${pretty.join(", ")}`;
}

function emptyCmsFields(
  identity: Pick<SnfDashboardFacility, "facility_id" | "ccn" | "name" | "city" | "state"> & {
    missing_sources?: string[];
  },
): SnfDashboardFacility {
  return {
    facility_id: identity.facility_id ?? null,
    ccn: identity.ccn,
    name: identity.name,
    city: identity.city,
    state: identity.state,
    overall_star_rating: null,
    pbj_quarter: null,
    hprd: null,
    active_f_tag_count: null,
    ij_count: null,
    survey_due_date: null,
    days_until_survey_due: null,
    estimated_adjustment_ytd_usd: null,
    missing_sources: identity.missing_sources ?? (identity.ccn ? [] : ["ccn"]),
  };
}

function facilitiesWithLocalIdentity(
  facilities: SnfDashboardFacility[],
  linkedSnfs: FacilityHierarchyInput[],
  meta?: NetworkDashboardMeta,
): SnfDashboardFacility[] {
  const rows = [...facilities];
  const presentIds = new Set(
    rows.map((row) => row.facility_id).filter((id): id is number => id != null),
  );
  const locals = meta?.local_facilities?.length
    ? meta.local_facilities.map((row) =>
        emptyCmsFields({
          facility_id: row.facility_id,
          ccn: row.ccn ?? null,
          name: row.name ?? null,
          city: row.city ?? null,
          state: row.state ?? null,
          missing_sources: row.missing_sources,
        }),
      )
    : facilities.length === 0
      ? linkedSnfs.map((facility) => {
          const ids = networkRegistryDisplayIds(facility);
          return emptyCmsFields({
            facility_id: getFacilityHierarchyId(facility),
            ccn: ids.ccn ?? null,
            name: facility.facility_name ?? null,
            city: typeof facility.city === "string" ? facility.city : null,
            state: typeof facility.state === "string" ? facility.state : null,
            missing_sources: ids.ccn ? [] : ["ccn"],
          });
        })
      : [];
  for (const extra of locals) {
    if (extra.facility_id != null && presentIds.has(extra.facility_id)) continue;
    if (extra.facility_id != null) presentIds.add(extra.facility_id);
    rows.push(extra);
  }
  return rows;
}

export function buildLiveSnfDashboardModel(
  data: SnfDashboardData,
  linkedSnfs: FacilityHierarchyInput[],
  meta?: NetworkDashboardMeta,
): SnfDashboardModel {
  const linkedByCcn = linkedFacilityByCcn(linkedSnfs);
  const linkedById = new Map<number, FacilityHierarchyInput>();
  linkedSnfs.forEach((facility) => {
    const id = getFacilityHierarchyId(facility);
    if (id != null) linkedById.set(id, facility);
  });
  const sourceFacilities = facilitiesWithLocalIdentity(
    data?.facilities ?? [],
    linkedSnfs,
    meta,
  );
  const roster: SnfRosterRow[] = sourceFacilities.map((row, index) => {
    const linked =
      linkedByCcn.get(normalizeCcn(row.ccn)) ??
      (row.facility_id != null ? linkedById.get(row.facility_id) : undefined);
    const facilityId = linked ? getFacilityHierarchyId(linked) ?? null : row.facility_id ?? null;
    const hprdLabel = formatHprd(row.hprd);
    return {
      key: facilityId != null ? `snf-${facilityId}` : `snf-idx-${index}`,
      facilityId,
      name: rowName(row, linked, index),
      metaLine:
        [
          row.ccn ? formatCcnLabel(row.ccn) : linked ? buildMetaLine(linked) : "CCN —",
          [row.city, row.state].filter(Boolean).join(", ") || null,
        ]
          .filter(Boolean)
          .join(" · ") || "CCN — · —",
      stars: row.overall_star_rating,
      hprdLabel,
      hprdTone: hprdTone(row.hprd),
      fTagsActive: row.active_f_tag_count,
      ijCount: row.ij_count,
      nextSurveyLabel: formatSurveyDays(row.days_until_survey_due),
      nextSurveyTone: surveyTone(row.days_until_survey_due),
      estimatedAdjustmentLabel: formatCurrency(row.estimated_adjustment_ytd_usd),
      missingSourcesLabel: missingSourcesLabel(
        row.missing_sources as string[] | undefined,
        row.ccn,
      ),
      dashboardFacility:
        facilityId == null
          ? null
          : toDashboardFacility(
              linked ?? {
                facility_name: row.name ?? undefined,
                city: row.city ?? undefined,
                state: row.state ?? undefined,
                facility_type: "SNF",
                facility_type_slug: "skilled_nursing_facility",
              },
              facilityId,
            ),
    };
  });

  roster.sort((a, b) => {
    if ((b.fTagsActive ?? -1) !== (a.fTagsActive ?? -1)) {
      return (b.fTagsActive ?? -1) - (a.fTagsActive ?? -1);
    }
    return (a.stars ?? 6) - (b.stars ?? 6);
  });

  const summary = snfSummary(data?.summary);
  const count = (value: number | null) => value == null ? "—" : String(value);
  const kpis: SnfKpiTile[] = [
    {
      id: "stars",
      label: "Avg overall star rating",
      value: formatStars(summary?.average_star_rating ?? null),
      sublabel: summary.rated_facility_count == null
        ? "Rated facilities unavailable"
        : `${summary.rated_facility_count} rated · lowest ${formatStars(summary.lowest_star_rating)}`,
    },
    {
      id: "hprd",
      label: "Below 3.0 HPRD",
      value: count(summary.below_3_hprd_count),
      sublabel: summary.hprd_facility_count == null
        ? "Per-resident-day staffing threshold"
        : `${summary.hprd_facility_count} facilities with HPRD data`,
    },
    {
      id: "ftags",
      label: "Active F-tag citations",
      value: count(summary.active_f_tag_count),
      sublabel: `${count(summary.ij_count)} IJ · ${count(summary.severity_d_plus_count)} severity D+ · ${count(summary.severity_a_c_count)} A–C`,
      valueTone: summary.active_f_tag_count == null ? "muted" : "error",
    },
    {
      id: "vbp",
      label: "SNF VBP adjustment YTD",
      value: formatCurrency(summary.estimated_vbp_adjustment_ytd_usd),
      sublabel: summary.federal_fiscal_year == null
        ? "Net readmission penalty impact"
        : `FY${summary.federal_fiscal_year} · ${count(summary.vbp_contributing_facility_count)} contributing facilities`,
    },
  ];

  return {
    facilityCount: Math.max(
      data?.total_count ?? summary.facility_count ?? 0,
      roster.length,
    ),
    kpis,
    roster,
  };
}

/** Backward-compatible alias for callers that only need an empty model. */
export const buildSnfDashboardModel = (linkedSnfs: FacilityHierarchyInput[]): SnfDashboardModel =>
  buildLiveSnfDashboardModel(
    {
      summary: {
        facility_count: linkedSnfs.length,
        average_star_rating: null,
        rated_facility_count: null,
        lowest_star_rating: null,
        below_3_hprd_count: null,
        hprd_facility_count: null,
        active_f_tag_count: null,
        ij_count: null,
        severity_d_plus_count: null,
        severity_a_c_count: null,
        estimated_vbp_adjustment_ytd_usd: null,
        vbp_contributing_facility_count: null,
        federal_fiscal_year: null,
      },
      facilities: linkedSnfs.map((facility) => ({
        ccn: networkRegistryDisplayIds(facility).ccn ?? null,
        name: facility.facility_name ?? null,
        city: typeof facility.city === "string" ? facility.city : null,
        state: typeof facility.state === "string" ? facility.state : null,
        overall_star_rating: null,
        pbj_quarter: null,
        hprd: null,
        active_f_tag_count: null,
        ij_count: null,
        survey_due_date: null,
        days_until_survey_due: null,
        estimated_adjustment_ytd_usd: null,
      })),
      total_count: linkedSnfs.length,
    },
    linkedSnfs,
  );

export function snfToneColor(tone: SnfTone): string {
  if (tone === "error") return "#a4262c";
  if (tone === "warn") return "#8b4409";
  if (tone === "muted") return "#737373";
  return "#323130";
}

export function isSnfTypeDashboard(typeCode: string | null | undefined): boolean {
  return String(typeCode || "").toUpperCase() === SNF_TYPE_CODE;
}
